import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Content,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@patternfly/react-core';
import { SyncAltIcon } from '@patternfly/react-icons';
import React, { useEffect, useState } from 'react';
import { useApiFetch } from '../../api';
import type { ApiResponseClientShow, ApiResponseClientWithdraw } from '../../types/api/ApiResponseClient';

export interface JoinPendingProps {
  clientID: number | null;
  refreshUser: () => Promise<void>;
}

/**
 * Join-pending screen. A user who joined an organization but isn't yet approved is backend-gated
 * (§3.5) to a few endpoints, so this dedicated screen — not the routed app — is shown. It fetches
 * the organization name for the copy (cosmetic; failures fall back to generic wording) and offers
 * two actions: Refresh status (re-fetch the user so an administrator's approval is picked up — it
 * does NOT self-approve) and Withdraw (confirm → POST /clients/withdraw → refreshUser → clientless).
 * There is no self-service approval — only an administrator can approve (TODO #18). Feedback is an
 * inline Alert; the notification drawer is not mounted on a gate screen.
 */
export function JoinPending({ clientID, refreshUser }: JoinPendingProps) {
  const apiFetch = useApiFetch();

  const [orgName, setOrgName] = useState<string | null>(null);
  const [isLoadingName, setIsLoadingName] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch the org name once on mount for the pending copy. The name is cosmetic: the empty
  // data:{} case (no client in scope) and any fetch/parse failure fall back to generic copy
  // rather than blocking the screen — the user can still withdraw.
  useEffect(() => {
    let cancelled = false;

    if (clientID === null) {
      // Shouldn't happen for a join-pending user, but guard it: skip the fetch, use generic copy.
      setIsLoadingName(false);
      return;
    }

    const fetchOrgName = async () => {
      try {
        const response = await apiFetch(`/clients/${clientID}`, {
          headers: { Accept: 'application/json' },
        });
        const body = (await response.json()) as ApiResponseClientShow;
        if (!cancelled && body.status === 'success' && body.data?.client?.name) {
          setOrgName(body.data.client.name);
        }
      } catch {
        // Non-critical — fall back to generic copy.
      } finally {
        if (!cancelled) {
          setIsLoadingName(false);
        }
      }
    };

    void fetchOrgName();

    return () => {
      cancelled = true;
    };
    // apiFetch identity isn't a meaningful re-fetch trigger; only clientID matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientID]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Re-fetch the user so the gate re-derives onboarding_state: if an administrator has approved
      // the request it becomes 'active' (this screen unmounts → the app); otherwise it stays pending.
      await refreshUser();
    } finally {
      setIsRefreshing(false);
    }
  };

  const closeModal = () => {
    if (isWithdrawing) {
      return; // don't dismiss mid-withdraw
    }
    setIsModalOpen(false);
    setWithdrawError(null);
  };

  const handleWithdraw = async () => {
    setIsWithdrawing(true);
    setWithdrawError(null);
    try {
      // POST /clients/withdraw takes NO body (§3.3).
      const response = await apiFetch('/clients/withdraw', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const data = (await response.json()) as ApiResponseClientWithdraw;
      // Success is status:'success' + data — never response.ok / 422 / 'fail'.
      if (data.status === 'success' && data.data) {
        await refreshUser(); // → clientless; this component unmounts (gate shows the create form)
        return;
      }
      setWithdrawError(data.message ?? 'We could not withdraw your request. Please try again.');
    } catch {
      setWithdrawError('We could not reach the server. Please check your connection and try again.');
    } finally {
      setIsWithdrawing(false);
    }
  };

  return (
    <>
      <Card style={{ maxWidth: '600px' }}>
        <CardTitle>Membership pending</CardTitle>
        <CardBody>
          {isLoadingName ? (
            <Spinner size="lg" aria-label="Loading organization details" />
          ) : orgName ? (
            <Content component="p">
              Your request to join <strong>{orgName}</strong> is pending. An administrator needs to approve it before
              you can access the workspace.
            </Content>
          ) : (
            <Content component="p">
              Your membership request is pending. An administrator needs to approve it before you can access the
              workspace.
            </Content>
          )}
        </CardBody>
        <CardFooter>
          <Button
            variant="secondary"
            icon={<SyncAltIcon />}
            onClick={() => void handleRefresh()}
            isLoading={isRefreshing}
            isDisabled={isRefreshing}
            spinnerAriaValueText="Refreshing status"
            className="pf-v6-u-mr-sm"
          >
            Refresh status
          </Button>
          <Button variant="link" onClick={() => setIsModalOpen(true)} isDisabled={isRefreshing}>
            Withdraw request
          </Button>
        </CardFooter>
      </Card>

      <Modal variant="small" isOpen={isModalOpen} onClose={closeModal} aria-labelledby="withdraw-modal-title">
        <ModalHeader title="Withdraw membership request" labelId="withdraw-modal-title" titleIconVariant="warning" />
        <ModalBody>
          {withdrawError && (
            <Alert variant="danger" isInline title="Could not withdraw request" className="pf-v6-u-mb-md">
              {withdrawError}
            </Alert>
          )}
          <Content component="p">
            Withdraw your pending request to join {orgName ?? 'this organization'}? You&apos;ll return to organization
            setup and can create or join an organization again.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="danger"
            onClick={() => void handleWithdraw()}
            isLoading={isWithdrawing}
            isDisabled={isWithdrawing}
            spinnerAriaValueText="Withdrawing request"
          >
            Withdraw
          </Button>
          <Button variant="link" onClick={closeModal} isDisabled={isWithdrawing}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
